import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

interface AuthContextValue {
  isLoggedIn: boolean;
  userName: string | null;
  userId: string | null;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  getAccessToken: () => Promise<string>;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  isLoggedIn: false,
  userName: null,
  userId: null,
  isAdmin: false,
  isSuperAdmin: false,
  getAccessToken: async () => "",
  login: async () => {},
  logout: async () => {}
});

const scopes = [process.env.REACT_APP_API_SCOPE!];

// Dev mode: bypass authentication for local development
const isDevMode = process.env.REACT_APP_DEV_MODE === "true";

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const [isLoggedIn, setIsLoggedIn] = useState(isDevMode);
  const [userName, setUserName] = useState<string | null>(
    isDevMode ? process.env.REACT_APP_DEV_USER_NAME || "Dev User" : null
  );
  const [userId, setUserId] = useState<string | null>(
    isDevMode ? process.env.REACT_APP_DEV_USER_ID || "dev-user-id" : null
  );
  const [isAdmin, setIsAdmin] = useState(isDevMode);
  const [isSuperAdmin, setIsSuperAdmin] = useState(isDevMode);

  const getAccessToken = useCallback(async () => {
    if (isDevMode) {
      console.log("[DevMode] Returning mock token");
      return "dev-mock-token";
    }

    if (!account) throw new Error("No account");
    try {
      const silent = await instance.acquireTokenSilent({
        account,
        scopes
      });
      return silent.accessToken;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        const interactive = await instance.acquireTokenPopup({
          account,
          scopes
        });
        return interactive.accessToken;
      }
      throw e;
    }
  }, [account, instance, isDevMode]);

  useEffect(() => {
    // Skip MSAL in dev mode
    if (isDevMode) return;

    if (account) {
      setIsLoggedIn(true);
      setUserName(account.name || account.username);
      setUserId(account.localAccountId);
      
      // Fetch user roles from backend
      (async () => {
        try {
          const token = await getAccessToken();
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/content/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.ok) {
            const user = await response.json();

            
            const isSuperAdminValue = Boolean(user.isSuperAdmin);
            const isAdminValue = Boolean(user.isAdmin) || isSuperAdminValue;
            setIsSuperAdmin(isSuperAdminValue);
            setIsAdmin(isAdminValue);
          } else {
            setIsAdmin(false);
            setIsSuperAdmin(false);
          }
        } catch (error) {
          setIsAdmin(false);
          setIsSuperAdmin(false);
        }
      })();
    } else {
      setIsLoggedIn(false);
      setUserName(null);
      setUserId(null);
      setIsAdmin(false);
      setIsSuperAdmin(false);
    }
  }, [account, getAccessToken]);

  const login = useCallback(async () => {
    if (isDevMode) {
      return;
    }
    await instance.loginPopup({ scopes });
  }, [instance, isDevMode]);

  const logout = useCallback(async () => {
    if (isDevMode) {
      return;
    }
    await instance.logoutPopup();
  }, [instance, isDevMode]);

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        userName,
        userId,
        isAdmin,
        isSuperAdmin,
        getAccessToken,
        login,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);